"use client";

import Image from "next/image";
import { useTranslations } from "next-intl";

const IMAGE_BASE = "/image/new-version/molding/injection-mold-tooling/types";
const DEFAULT_IMAGES = [
	`${IMAGE_BASE}/1.webp`,
	`${IMAGE_BASE}/2.jpg`,
	`${IMAGE_BASE}/3.webp`,
	`${IMAGE_BASE}/4.jpg`,
];

const ROW_KEYS = ["singleCavity", "multiCavity", "stack", "twoK"] as const;

export interface TypesOfInjectionMoldsProps {
	/** 翻译命名空间，需包含 title、intro、各 row 的 titleKey/descKey、getQuoteButton、uploadButton、imageAlt1～4 */
	translationNamespace: string;
	/** 四张图片路径，不传则用 Types of Injection Molds/1～4.webp */
	imageUrls?: string[];
}

/**
 * Types of Injection Molds：居中标题+绿条+引言，2x2 卡片网格（每卡上图下文），图片占满卡片宽度，底部双按钮
 */
export default function TypesOfInjectionMolds({
	translationNamespace,
	imageUrls = DEFAULT_IMAGES,
}: TypesOfInjectionMoldsProps) {
	const t = useTranslations(translationNamespace);

	return (
		<section className="w-full bg-white py-8 sm:py-10 md:py-12 lg:py-16">
			<div className="container">
				{/* 居中标题 + 绿条 + 引言 */}
				<div className="mx-auto text-center">
					<div className="mb-3 flex flex-wrap items-center justify-center gap-3 sm:mb-4">
						<h2 className="text-2xl font-bold leading-tight text-black sm:text-[30px] md:text-4xl">
							{t("title")}
						</h2>
						<div className="h-1 w-10 shrink-0 rounded bg-themePrimary400" aria-hidden />
					</div>
					<p className="text-sm leading-relaxed text-black md:text-base">{t("intro")}</p>
				</div>

				{/* 2x2 网格：每卡上图（占满宽度）下文 */}
				<div className="mt-6 grid grid-cols-1 gap-6 sm:mt-8 sm:grid-cols-2 md:mt-10 md:gap-8 lg:mt-12">
					{ROW_KEYS.map((key, index) => (
						<div key={key} className="overflow-hidden rounded-[12px] border border-gray-200 bg-white">
							{/* 图片区：浅灰底 + 内边距，图片 object-contain 不裁剪 */}
							<div className="flex items-center justify-center bg-[#f5f5f5]">
								<div className="relative aspect-[4/3] w-full max-w-[280px] md:max-w-[320px]">
									<Image
										src={imageUrls[index] ?? imageUrls[0]}
										alt={
											index === 0
												? t("imageAlt1")
												: index === 1
													? t("imageAlt2")
													: index === 2
														? t("imageAlt3")
														: t("imageAlt4")
										}
										fill
										className="object-contain"
										sizes="(max-width: 640px) 280px, 320px"
									/>
								</div>
							</div>
							{/* 文案区：白底、左对齐 */}
							<div className="p-5 md:p-6">
								<h3 className="mb-3 text-left text-lg font-bold text-black sm:text-xl md:text-2xl">
									{t(`${key}Title`)}
								</h3>
								<p className="text-left text-sm leading-relaxed text-black md:text-base">{t(`${key}Desc`)}</p>
							</div>
						</div>
					))}
				</div>

				{/* 底部双按钮 */}
				<div className="mt-12 flex flex-wrap items-center justify-center gap-4 md:mt-16">
					<a
						href="/upload"
						target="/upload"
						rel="noopener noreferrer"
						className="inline-flex items-center justify-center rounded-lg bg-themePrimary400 px-6 py-3 text-sm font-semibold text-white transition hover:opacity-90 md:px-8 md:py-3.5 md:text-base"
					>
						{t("getQuoteButton")}
					</a>
					<a
						href="/upload"
						target="_blank"
						rel="noopener noreferrer"
						className="inline-flex items-center justify-center rounded-lg border-2 border-themePrimary400 bg-white px-6 py-3 text-sm font-semibold text-themePrimary400 transition hover:bg-themePrimary400/5 md:px-8 md:py-3.5 md:text-base"
					>
						{t("uploadButton")}
					</a>
				</div>
			</div>
		</section>
	);
}
